/**
 * POST and GET against the backend, shaped like the legacy jQuery calls: a
 * 10 second timeout, an optional bearer token, a JSON response, and
 * form-encoded request bodies. Callers name the endpoint; the host is
 * BACKEND_HOST. Both throw on any failure, so fire-and-forget callers must
 * catch.
 */
import { BACKEND_HOST } from './Constants';

const AJAX_TIMEOUT_MS = 10000;

/** What jQuery put on every POST; the backend has only ever been fed this. */
const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=UTF-8';

/** The backend endpoints the game calls. The games list is the GamesData service's. */
export type BackendPath = '/settings' | '/enter' | '/clienterror';

/** A form field value. null and undefined are sent as an empty string, as jQuery sent them. */
export type FormFieldValue = string | number | boolean | null | undefined;

/** The flat key/value shape every POST in the client sends. */
export type FormFields = Record<string, FormFieldValue>;

/** Encodes fields the way jQuery.param did for a flat object. */
function encodeForm(fields: FormFields): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    params.append(key, value == null ? '' : String(value));
  }
  return params.toString();
}

/**
 * POST with optional Bearer auth. An object is sent as form fields; a string
 * is sent as-is under the form content type, which is how legacy posted the
 * settings JSON. Throws on any failure.
 */
export function ajaxPost<T = unknown>(path: BackendPath, data: string | FormFields, token?: string): Promise<T> {
  return request<T>('POST', path, typeof data === 'string' ? data : encodeForm(data), token);
}

/** GET with optional Bearer auth. Throws on any failure. */
export function ajaxGet<T = unknown>(path: BackendPath, token?: string): Promise<T> {
  return request<T>('GET', path, undefined, token);
}

/**
 * Rejects with a TimeoutError after AJAX_TIMEOUT_MS, an Error on a non-2xx
 * status, and whatever fetch or the JSON parse throws otherwise.
 */
async function request<T>(
  method: 'GET' | 'POST',
  path: BackendPath,
  body: string | undefined,
  token: string | undefined,
): Promise<T> {
  // jQuery's dataType: 'json' asked for JSON; the backend answers JSON either way.
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) {
    headers['Content-Type'] = FORM_CONTENT_TYPE;
  }

  if (token !== undefined) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`https://${BACKEND_HOST}${path}`, {
    method,
    headers,
    body,
    signal: AbortSignal.timeout(AJAX_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const json: unknown = await response.json();
  return json as T;
}
